import { computed } from 'vue';
import { useMutation, useQuery } from '@vue/apollo-composable';
import { graphql } from '@/gql';
import { useRoute, useRouter } from 'vue-router';
import { useUserSettingsStore } from '@/stores/user-settings';

import type { FormSave } from '../gql/graphql';

export enum FormName {
    'project_edit' = 'project_edit',
    'deliverable_create' = 'deliverable_create',
    'deliverable_edit' = 'deliverable_edit',
    'bottleneck_create' = 'bottleneck_create',
    'bottleneck_edit' = 'bottleneck_edit',
    'bottleneck_escalate' = 'bottleneck_escalate',
    'escalation_create' = 'escalation_create',
    'escalation_edit' = 'escalation_edit',
    'phase_create' = 'phase_create',
}

type FormSaveQueryParam = [Key: string, Value?: string];

export interface CreateFormSaveInput {
    fields: object;
    name: FormName;
    path?: string;
    queryParams?: FormSaveQueryParam[];
}

export function useFormSession() {
    const { path } = useRoute();
    const router = useRouter();
    const userSettingsStore = useUserSettingsStore();

    const { result, loading, refetch, onResult } = useQuery(
        graphql(`
            query SavedSession {
                currentUser {
                    formSaves {
                        formFields
                        url
                        portfolio {
                            id
                        }
                        id
                        formName
                    }
                }
            }
        `)
    );

    const { mutate: mutateDeleteFormSave } = useMutation(
        graphql(`
            mutation DeleteFormSave($input: DeleteFormSaveInput!) {
                deleteFormSave(input: $input) {
                    ok
                }
            }
        `)
    );

    const { mutate } = useMutation(
        graphql(`
            mutation CreateFormSave($input: CreateFormSaveInput!) {
                createFormSave(input: $input) {
                    ok
                }
            }
        `)
    );

    async function clearFormSave() {
        if (!id.value) return;

        try {
            await mutateDeleteFormSave({
                input: {
                    formSaveId: id.value,
                },
            });
            refetch();
        } catch (err) {
            console.error(err);
        } finally {
            router.push({
                name: path,
                query: {},
            });
        }
    }

    async function createFormSave(input: CreateFormSaveInput) {
        try {
            let fName = input.name.toString();

            if (input.queryParams?.length) {
                input.queryParams.forEach(([q, v]) => {
                    fName += `&${q}=${v}`;
                });
            }

            const response = await mutate({
                input: {
                    formFields: JSON.stringify(input.fields),
                    formName: fName,
                    portfolioId: userSettingsStore.activePortfolio!.id,
                    url: input.path ?? path,
                },
            });

            refetch();

            return response;
        } catch (err) {
            console.error(err);
        }
    }

    function buildQueryParams(params: FormSaveQueryParam[]) {
        return params.filter(([_, value]) => value !== undefined);
    }

    function extractFormSave(formSaves: FormSave[]): null | object {
        if (!formSaves?.[0]?.formFields) return null;

        return JSON.parse(formSaves[0].formFields, (_key, value) => {
            // Transform date strings into date objects
            if (
                typeof value === 'string' &&
                /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z$/.test(value)
            ) {
                const date = new Date(value);
                return isNaN(date.getTime()) ? value : date;
            }
            return value;
        });
    }

    const formSave = computed<FormSave>(
        () => result?.value?.currentUser?.formSaves?.[0] || null
    );
    const fields = computed(() =>
        extractFormSave(result.value?.currentUser?.formSaves)
    );
    const url = computed(() => formSave.value?.url);
    const id = computed(() => formSave.value?.id);
    const portfolioId = computed(() => formSave.value?.portfolio?.id);
    const name = computed(() => formSave.value?.formName);

    const session = computed(() =>
        formSave.value
            ? {
                  url: formSave.value.url,
                  id: formSave.value.id,
                  portfolioId: formSave.value.portfolio?.id,
                  name: formSave.value.formName,
                  fields: fields.value,
              }
            : null
    );

    return {
        fields,
        loading,
        onResult,
        url,
        id,
        name,
        portfolioId,
        session,
        createFormSave,
        clearFormSave,
        buildQueryParams,
    };
}
